import { alpha, useTheme } from '@mui/material/styles'
import Skeleton from '@mui/material/Skeleton'
import {
  Client,
  type Conversation,
  type Message,
  type Participant,
} from '@twilio/conversations'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ChatConversation,
  TherapistInfo,
  PatientInfo,
} from '../services/chatApi'
import {
  listTherapistConversations,
  listPatientConversations,
  listTherapistConversationsForAgency,
  listPatientConversationsForAgency,
  requestTherapistToken,
  requestPatientToken,
  requestAgencyToken,
  resolveTwilioConversationSid,
  isTwilioConversationSid,
} from '../services/chatApi'
import useAuth from '@/hooks/useAuth'
import { UserRole } from '@/types'

interface ConversationListProps {
  onSelectConversation: (conversation: ChatConversation) => void
  selectedConversationSid?: string
}

const LIST_REFRESH_MS = 12000
const TYPING_TTL_MS = 6000

const getParticipantName = (participant: unknown): string => {
  if (!participant || typeof participant === 'string') {
    return 'Deleted user'
  }

  const p = participant as {
    firstName?: string
    lastName?: string
    name?: string
  }

  const first = p.firstName ?? p.name ?? ''
  const last = p.lastName ?? ''
  const fullName = `${first} ${last}`.trim()

  return fullName || 'Deleted user'
}

const getParticipantAvatar = (participant: unknown): string | undefined => {
  if (!participant || typeof participant === 'string') return undefined

  const p = participant as {
    profilePhotoUrl?: string
    avatarUrl?: string
    avatar?: string
  }

  return p.profilePhotoUrl || p.avatarUrl || p.avatar
}

const getAgencyName = (agency: unknown): string => {
  if (!agency || typeof agency === 'string') return 'Deleted agency'
  const a = agency as { agencyName?: string; name?: string }
  return a.agencyName ?? a.name ?? 'Deleted agency'
}

const getAgencyAvatar = (agency: unknown): string | undefined => {
  if (!agency || typeof agency === 'string') return undefined
  const a = agency as { profilePhotoUrl?: string; avatarUrl?: string }
  return a.profilePhotoUrl || a.avatarUrl
}

const formatLastMessageTime = (iso?: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const isSameDay = now.toDateString() === date.toDateString()

  if (isSameDay) {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const getConversationSid = (conversation: ChatConversation): string =>
  resolveTwilioConversationSid(
    conversation.conversationSid,
    conversation.conversationId
  )

interface ConversationLiveMeta {
  lastMessage?: string
  lastMessageAt?: string
  unreadCount?: number
}

export const ConversationList = ({
  onSelectConversation,
  selectedConversationSid,
}: ConversationListProps) => {
  const theme = useTheme()
  const { user } = useAuth()
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [patientProviders, setPatientProviders] = useState<ChatConversation[]>([])
  const [patientAgencies, setPatientAgencies] = useState<ChatConversation[]>([])
  const [therapistPatients, setTherapistPatients] = useState<ChatConversation[]>([])
  const [therapistAgencies, setTherapistAgencies] = useState<ChatConversation[]>([])
  const [liveMetaByConversationSid, setLiveMetaByConversationSid] = useState<
    Record<string, ConversationLiveMeta>
  >({})
  const [typingByConversationSid, setTypingByConversationSid] = useState<
    Record<string, boolean>
  >({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const typingTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({})
  const listRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const isTherapist = user?.role === UserRole.THERAPIST
  const isAgency = user?.role === UserRole.AGENCY
  const userId = user?.id
  const [agencyTab, setAgencyTab] = useState<'patients' | 'providers'>('patients')
  const [patientTab, setPatientTab] = useState<'providers' | 'agencies'>('providers')
  const [therapistTab, setTherapistTab] = useState<'patients' | 'agencies'>('patients')

  const resolveAgencyId = () => {
    // Prefer explicit agencyId fields, fall back to user id
    // User object shapes vary across the app so try multiple properties
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const u = user as any
    return (
      u?.agencyId || (typeof u?.agency === 'string' ? u?.agency : u?.agency?._id) ||
      userId || ''
    )
  }

  const fetchConversations = useCallback(
    async (showLoading = false) => {
      if (!userId) {
        setConversations([])
        setLoading(false)
        return
      }

      try {
        if (showLoading) {
          setLoading(true)
          setError(null)
        }

        let data: any = []

        if (isAgency) {
          const agencyId = resolveAgencyId()
          if (!agencyId) throw new Error('Missing agency id')

          data =
            agencyTab === 'providers'
              ? await listTherapistConversationsForAgency(agencyId)
              : await listPatientConversationsForAgency(agencyId)
        } else {
          data = isTherapist
            ? await listTherapistConversations(userId as string)
            : await listPatientConversations(userId as string)
        }

        // Helper to normalize individual items
        const normalize = (items: any[] = []) =>
          (items || []).map((c: any) => {
            const cv: any = { ...c }

            if (cv.patientId && typeof cv.patientId === 'object') {
              const p = cv.patientId as any
              cv.patientDisplayName = `${(p.firstName || '').trim()} ${(p.lastName || '').trim()}`.trim() || undefined
              cv.patientAvatar = p.profilePhotoUrl || p.avatarUrl || p.avatar || undefined
            }

            if (cv.therapistId && typeof cv.therapistId === 'object') {
              const t = cv.therapistId as any
              cv.therapistDisplayName = `${(t.firstName || '').trim()} ${(t.lastName || '').trim()}`.trim() || undefined
              cv.therapistAvatar = t.profilePhotoUrl || t.avatarUrl || t.avatar || undefined
            }

            if (cv.agencyId && typeof cv.agencyId === 'object') {
              const a = cv.agencyId as any
              cv.agencyDisplayName = a.agencyName || undefined
              cv.agencyAvatar = a.profilePhotoUrl || undefined
            }

            return cv
          })

        let normalized: any[] = []

        // If patient endpoint returns separate arrays, keep them separate
        if (!Array.isArray(data) && data && !isTherapist && !isAgency) {
          const therapistsRaw = Array.isArray((data as any).therapist) ? (data as any).therapist : []
          const agenciesRaw = Array.isArray((data as any).agency) ? (data as any).agency : []

          // Filter out conversations that are not populated for the respective tab
          const therapists = normalize(therapistsRaw.filter(c => c.therapistId && typeof c.therapistId === 'object'))
          const agencies = normalize(agenciesRaw.filter(c => c.agencyId && typeof c.agencyId === 'object'))

          setPatientProviders(therapists)
          setPatientAgencies(agencies)

          normalized = [...therapists, ...agencies]
        } else if (!Array.isArray(data) && data && isTherapist) {
          const patientsRaw = Array.isArray((data as any).patient) ? (data as any).patient : []
          const agenciesRaw = Array.isArray((data as any).agency) ? (data as any).agency : []

          // Filter out conversations that are not populated for the respective tab
          const patients = normalize(patientsRaw.filter(c => c.patientId && typeof c.patientId === 'object'))
          const agencies = normalize(agenciesRaw.filter(c => c.agencyId && typeof c.agencyId === 'object'))

          setTherapistPatients(patients)
          setTherapistAgencies(agencies)

          normalized = [...patients, ...agencies]
        } else {
          normalized = normalize(Array.isArray(data) ? data : [])
        }

        setConversations(normalized)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load conversations'
        )
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [isTherapist, userId, isAgency, agencyTab, patientTab, therapistTab]
  )

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    void fetchConversations(true)
    const interval = setInterval(() => {
      void fetchConversations(false)
    }, LIST_REFRESH_MS)

    return () => {
      clearInterval(interval)
    }
  }, [fetchConversations, userId, patientTab, therapistTab])

  useEffect(() => {
    if (!selectedConversationSid) return
    setLiveMetaByConversationSid(prev => {
      if (!prev[selectedConversationSid]) return prev
      return {
        ...prev,
        [selectedConversationSid]: {
          ...prev[selectedConversationSid],
          unreadCount: 0,
        },
      }
    })
  }, [selectedConversationSid])

  useEffect(() => {
    if (!userId || conversations.length === 0) {
      setTypingByConversationSid({})
      return
    }

    let isActive = true
    let client: Client | null = null
    const detachFns: Array<() => void> = []

    const clearTypingForSid = (conversationSid: string) => {
      const timer = typingTimeoutsRef.current[conversationSid]
      if (timer) {
        clearTimeout(timer)
        delete typingTimeoutsRef.current[conversationSid]
      }

      setTypingByConversationSid(prev => {
        if (!prev[conversationSid]) return prev
        const next = { ...prev }
        delete next[conversationSid]
        return next
      })
    }

    const markTypingForSid = (conversationSid: string) => {
      setTypingByConversationSid(prev => ({ ...prev, [conversationSid]: true }))

      const existingTimer = typingTimeoutsRef.current[conversationSid]
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      typingTimeoutsRef.current[conversationSid] = setTimeout(() => {
        clearTypingForSid(conversationSid)
      }, TYPING_TTL_MS)
    }

    const scheduleListRefresh = () => {
      if (listRefreshDebounceRef.current) {
        clearTimeout(listRefreshDebounceRef.current)
      }
      listRefreshDebounceRef.current = setTimeout(() => {
        void fetchConversations(false)
      }, 500)
    }

    const initialiseRealtime = async () => {
      try {
        let tokenResponse: { token: string; identity?: string } | null = null
        if (isAgency) {
          const agencyId = resolveAgencyId()
          if (!agencyId) throw new Error('Missing agency id')
          tokenResponse = await requestAgencyToken(agencyId)
        } else {
          tokenResponse = isTherapist
            ? await requestTherapistToken(userId)
            : await requestPatientToken(userId)
        }

        if (!isActive) return

        const myIdentity = tokenResponse.identity ?? null
        client = new Client(tokenResponse.token)

        const sids = Array.from(
          new Set(
            conversations
              .map(getConversationSid)
              .filter(isTwilioConversationSid)
          )
        )

        await Promise.all(
          sids.map(async sid => {
            if (!client) return
            let twilioConversation: Conversation | null = null

            try {
              twilioConversation = await client.getConversationBySid(sid)
            } catch {
              setLiveMetaByConversationSid(prev => ({
                ...prev,
                [sid]: { ...prev[sid] },
              }))
              return
            }

            if (!twilioConversation) return

            const handleTypingStarted = (participant: Participant) => {
              if (myIdentity && participant.identity === myIdentity) return
              markTypingForSid(sid)
            }

            const handleTypingEnded = (participant: Participant) => {
              if (myIdentity && participant.identity === myIdentity) return
              clearTypingForSid(sid)
            }

            const handleMessageAdded = (message: Message) => {
              clearTypingForSid(sid)
              const body = (message.body ?? '').trim()
              const createdAt =
                message.dateCreated?.toISOString() ?? new Date().toISOString()
              const isOwnMessage = Boolean(
                myIdentity && message.author === myIdentity
              )

              setLiveMetaByConversationSid(prev => {
                const previous = prev[sid]
                const isViewingTherapists =
                  isTherapist || (isAgency && agencyTab === 'providers')

                const baseUnread =
                  previous?.unreadCount ??
                  (isAgency
                    ? ((conversations.find(c => getConversationSid(c) === sid) as any)?.agencyUnread ?? 0)
                    : isTherapist
                      ? (conversations.find(c => getConversationSid(c) === sid)?.therapistUnread ?? 0)
                      : (conversations.find(c => getConversationSid(c) === sid)?.patientUnread ?? 0))

                const nextUnread =
                  sid === selectedConversationSid || isOwnMessage
                    ? 0
                    : baseUnread + 1

                return {
                  ...prev,
                  [sid]: {
                    ...previous,
                    lastMessage: body || previous?.lastMessage,
                    lastMessageAt: createdAt,
                    unreadCount: nextUnread,
                  },
                }
              })

              scheduleListRefresh()
            }

            const handleMessageUpdated = () => {
              scheduleListRefresh()
            }

            twilioConversation.on('typingStarted', handleTypingStarted)
            twilioConversation.on('typingEnded', handleTypingEnded)
            twilioConversation.on('messageAdded', handleMessageAdded)
            twilioConversation.on('messageUpdated', handleMessageUpdated)

            void (async () => {
              try {
                const unread = await twilioConversation.getUnreadMessagesCount()
                const paginator = await twilioConversation.getMessages(1)
                const last = paginator.items[paginator.items.length - 1]

                setLiveMetaByConversationSid(prev => ({
                  ...prev,
                  [sid]: {
                    ...prev[sid],
                    lastMessage: last?.body ?? prev[sid]?.lastMessage,
                    lastMessageAt:
                      last?.dateCreated?.toISOString() ??
                      prev[sid]?.lastMessageAt,
                    unreadCount:
                      sid === selectedConversationSid
                        ? 0
                        : (unread ?? prev[sid]?.unreadCount ?? 0),
                  },
                }))
              } catch {
                setLiveMetaByConversationSid(prev => ({
                  ...prev,
                  [sid]: { ...prev[sid] },
                }))
              }
            })()

            detachFns.push(() => {
              twilioConversation?.off('typingStarted', handleTypingStarted)
              twilioConversation?.off('typingEnded', handleTypingEnded)
              twilioConversation?.off('messageAdded', handleMessageAdded)
              twilioConversation?.off('messageUpdated', handleMessageUpdated)
            })
          })
        )
      } catch {
        // Keep list functional even if realtime listeners fail.
      }
    }

    void initialiseRealtime()

    return () => {
      isActive = false
      detachFns.forEach(detach => detach())

      Object.values(typingTimeoutsRef.current).forEach(timer => {
        clearTimeout(timer)
      })
      typingTimeoutsRef.current = {}

      if (listRefreshDebounceRef.current) {
        clearTimeout(listRefreshDebounceRef.current)
        listRefreshDebounceRef.current = null
      }

      client?.shutdown().catch(() => {
        /* noop */
      })
    }
  }, [
    conversations,
    fetchConversations,
    isTherapist,
    isAgency,
    agencyTab,
    patientTab,
    therapistTab,
    selectedConversationSid,
    userId,
  ])

  const orderedConversations = useMemo(
    () =>
      [...conversations].sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
        return bTime - aTime
      }),
    [conversations]
  )

  const visibleConversations = useMemo(() => {
    // For agency view we show the full list (agency view will
    // rely on the agencyTab to fetch the appropriate items).
    if (isAgency) return orderedConversations

    if (isTherapist) {
      if (therapistTab === 'patients') {
        return [...therapistPatients].sort((a, b) => {
          const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
          const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
          return bTime - aTime
        })
      }
      return [...therapistAgencies].sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
        return bTime - aTime
      })
    }

    // For patients use the dedicated arrays populated from the API
    if (patientTab === 'providers') {
      return [...patientProviders].sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
        return bTime - aTime
      })
    }

    return [...patientAgencies].sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
      return bTime - aTime
    })
  }, [orderedConversations, isAgency, isTherapist, patientTab, patientProviders, patientAgencies, therapistTab, therapistPatients, therapistAgencies])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-8 h-8 border-4 rounded-full animate-spin"
          style={{
            borderColor: alpha(theme.palette.primary.main, 0.2),
            borderTopColor: theme.palette.primary.main,
          }}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full pb-4 overflow-y-auto">
      {isAgency && (
        <div className="flex items-center gap-3 p-3 bg-white rounded-md shadow-sm">
          <button
            aria-pressed={agencyTab === 'patients'}
            onClick={() => setAgencyTab('patients')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              agencyTab === 'patients'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Patients
          </button>
          <button
            aria-pressed={agencyTab === 'providers'}
            onClick={() => setAgencyTab('providers')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              agencyTab === 'providers'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Providers
          </button>
        </div>
      )}

      {isTherapist && (
        <div className="flex items-center gap-3 p-3 bg-white rounded-md shadow-sm">
          <button
            aria-pressed={therapistTab === 'patients'}
            onClick={() => setTherapistTab('patients')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              therapistTab === 'patients'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Patients
          </button>
          <button
            aria-pressed={therapistTab === 'agencies'}
            onClick={() => setTherapistTab('agencies')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              therapistTab === 'agencies'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Agencies
          </button>
        </div>
      )}

      {!isTherapist && !isAgency && (
        <div className="flex items-center gap-3 p-3 bg-white rounded-md shadow-sm">
          <button
            aria-pressed={patientTab === 'providers'}
            onClick={() => setPatientTab('providers')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              patientTab === 'providers'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Providers
          </button>
          <button
            aria-pressed={patientTab === 'agencies'}
            onClick={() => setPatientTab('agencies')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none flex items-center gap-2`}
            style={
              patientTab === 'agencies'
                ? {
                  backgroundColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: '#ffffff',
                }
                : { backgroundColor: 'transparent', border: '1px solid #e6e9ee', color: '#111827' }
            }
          >
            Agencies
          </button>
        </div>
      )}

      {visibleConversations.length === 0 && (
        <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
          <p>No conversations yet</p>
        </div>
      )}

      {visibleConversations.map(conversation => {
        // Prefer normalized display fields populated from server responses
        let participantName: string | undefined
        let participantAvatar: string | undefined

        if (isAgency) {
          if (agencyTab === 'providers') {
            participantName = (conversation as any).therapistDisplayName ?? getParticipantName(conversation.therapistId)
            participantAvatar = (conversation as any).therapistAvatar ?? getParticipantAvatar(conversation.therapistId)
          } else {
            participantName = (conversation as any).patientDisplayName ?? getParticipantName(conversation.patientId)
            participantAvatar = (conversation as any).patientAvatar ?? getParticipantAvatar(conversation.patientId)
          }
        } else {
          if (isTherapist) {
            if (therapistTab === 'patients') {
              participantName = (conversation as any).patientDisplayName ?? getParticipantName(conversation.patientId)
              participantAvatar = (conversation as any).patientAvatar ?? getParticipantAvatar(conversation.patientId)
            } else {
              participantName = (conversation as any).agencyDisplayName ?? getAgencyName(conversation.agencyId)
              participantAvatar = (conversation as any).agencyAvatar ?? getAgencyAvatar(conversation.agencyId)
            }
          } else {
            // Patient view: show providers or agencies based on patientTab
            if (patientTab === 'providers') {
              participantName = (conversation as any).therapistDisplayName ?? getParticipantName(conversation.therapistId)
              participantAvatar = (conversation as any).therapistAvatar ?? getParticipantAvatar(conversation.therapistId)
            } else {
              participantName = (conversation as any).agencyDisplayName ?? getAgencyName(conversation.agencyId)
              participantAvatar = (conversation as any).agencyAvatar ?? getAgencyAvatar(conversation.agencyId)
            }
          }
        }

        const conversationSid = getConversationSid(conversation)
        const isSelected = conversationSid === selectedConversationSid
        const liveMeta = liveMetaByConversationSid[conversationSid]
        const unreadCount =
          liveMeta?.unreadCount ??
          (isAgency
            ? ((conversation as any).agencyUnread ?? 0)
            : isTherapist
              ? (conversation.therapistUnread ?? 0)
              : (conversation.patientUnread ?? 0))
        const hasTyping = Boolean(typingByConversationSid[conversationSid])
        const lastMessageBody =
          liveMeta?.lastMessage ?? conversation.lastMessage ?? ''
        const isTwilioConversation = isTwilioConversationSid(conversationSid)
        const hasLiveMeta = conversationSid in liveMetaByConversationSid
        const isLoadingLastMessage = isTwilioConversation && !hasLiveMeta

        return (
          <button
            key={conversationSid || conversation._id || participantName}
            onClick={() =>
              onSelectConversation(
                isAgency
                  ? { ...(conversation as any), _selectedAgencyTab: agencyTab }
                  : isTherapist
                    ? { ...(conversation as any), _selectedTherapistTab: therapistTab }
                    : { ...(conversation as any), _selectedPatientTab: patientTab }
              )
            }
            className={`flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left transition hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''
              }`}
            style={
              isSelected
                ? { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                : undefined
            }
          >
            {participantAvatar ? (
              <img
                src={participantAvatar}
                alt={participantName}
                className="object-cover w-12 h-12 rounded-full shadow"
              />
            ) : (
              <div className="flex items-center justify-center w-12 h-12 text-sm font-semibold rounded-full shadow bg-slate-200 text-slate-600">
                {participantName && participantName.trim() !== ''
                  ? participantName
                    .split(' ')
                    .filter(Boolean)
                    .map(n => n[0] ?? '')
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                  : 'DU'}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate text-slate-800">
                {participantName}
              </p>
              {isLoadingLastMessage ? (
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '0.875rem' }}
                  width="70%"
                />
              ) : (
                <p
                  className={`text-sm truncate ${hasTyping
                      ? 'text-emerald-600 font-medium'
                      : unreadCount > 0
                        ? 'text-slate-700 font-medium'
                        : 'text-slate-500'
                    }`}
                >
                  {hasTyping
                    ? 'Typing...'
                    : lastMessageBody || 'No messages yet'}
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-1">
              {(liveMeta?.lastMessageAt || conversation.lastMessageAt) && (
                <p className="text-xs text-slate-400">
                  {formatLastMessageTime(
                    liveMeta?.lastMessageAt || conversation.lastMessageAt
                  )}
                </p>
              )}
              {unreadCount > 0 && (
                <span
                  className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white rounded-full"
                  style={{ backgroundColor: theme.palette.primary.main }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
